import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  DialogActions,
  Button,
  DialogProps,
  Breakpoint,
  Divider,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  GetShipmentQuery,
  S3_Operation,
  useGetSignedUrlMutation,
} from "../../../../graphql/generated";
import AddButton from "../../../common/AddButton";
import AddIcon from "@mui/icons-material/Add";
import ShipmentDocumentForm from "./ShipmentDocumentForm";
import { useDispatch } from "react-redux";
import { showDialog } from "../../../../redux/slices/alert/Alert.slice";
import {
  FieldErrors,
  FormError,
  ShipmentDocumentInputData,
} from "../../../../redux/slices/Types";
import { isEmpty } from "lodash";
import { v4 as uuid } from "uuid";
import { defaultShipmentDocument } from "../../../../redux/slices/shipment/ShipmentForm.slice";
import { useTranslation } from "react-i18next";
import { testIds } from "./ShipmentDocumentsUploadModal.testIds";

interface ShipmentDocumentsUploadModalProps {
  openModal: boolean;
  width?: Breakpoint;
  onClose: (document: Array<ShipmentDocumentInputData>) => void;
  shipmentLocations: GetShipmentQuery["shipmentById"]["shipmentLocations"];
  currentDocumentToEdit: ShipmentDocumentInputData | null;
  customerDocumentRequirements?: GetShipmentQuery["shipmentById"]["customer"]["documentRequirements"];
}

export default function ShipmentDocumentsUploadModal({
  openModal,
  width,
  onClose,
  shipmentLocations,
  currentDocumentToEdit,
  customerDocumentRequirements,
}: ShipmentDocumentsUploadModalProps) {
  const { t } = useTranslation(["orders", "common"]);
  const dispatch = useDispatch();
  const [documents, setDocuments] = useState<Array<ShipmentDocumentInputData>>(
    []
  );
  const [maxWidth] = useState<DialogProps["maxWidth"]>(width);
  const [errors, setErrors] = useState<Array<FormError>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const signUrlMutation = useGetSignedUrlMutation();

  useEffect(() => {
    if (openModal && !documents.length) {
      if (currentDocumentToEdit) {
        setDocuments([currentDocumentToEdit]);
      } else {
        setDocuments([{ ...defaultShipmentDocument, _id: uuid() }]);
      }
    }
  }, [openModal, documents, currentDocumentToEdit]);

  const handleChange = (id: number, document: ShipmentDocumentInputData) => {
    const _documents = [...documents];
    _documents[id] = document;
    setDocuments(_documents);
  };

  const handleUpload = async (
    id: number,
    file: File | null,
    error?: string
  ) => {
    if (!error && file) {
      try {
        setIsUploading(true);
        const signingResult = await signUrlMutation.mutateAsync({
          document: {
            operation: S3_Operation.PutObject,
            extension: file.name.split(".").pop(),
          },
        });
        const getSigningResult = await signUrlMutation.mutateAsync({
          document: {
            operation: S3_Operation.GetObject,
            key: signingResult.documentSignedUrl.key,
          },
        });
        await fetch(signingResult.documentSignedUrl.url, {
          method: "PUT",
          body: file,
        });
        handleChange(id, {
          ...documents[id],
          url: getSigningResult.documentSignedUrl.url,
          file,
        });
        setIsUploading(false);
        return;
      } catch (error) {
        setIsUploading(false);
        dispatch(
          showDialog({
            type: "error",
            title: t(
              "common:documents.errors.uploadError",
              "Error while uploading document"
            ),
            description:
              (error as FieldErrors).message ||
              t("common:error.unknownError", "Unknown error"),
          })
        );
      }
    }
  };

  const handleAddDocument = () => {
    setDocuments([...documents, { ...defaultShipmentDocument, _id: uuid() }]);
  };

  const handleCancel = () => {
    setDocuments([]);
    onClose([]);
  };

  const handleSubmit = () => {
    if (documents.length) {
      const errors: Array<FormError> = [];
      for (const document of documents) {
        const err: FieldErrors = {};
        if (!document.name) {
          err["name"] = t(
            "common:documents.errors.emptyLabel",
            "Document label cannot be empty"
          );
        }

        if (!document.url) {
          err["url"] = t(
            "common:documents.errors.attachFile",
            "Please attach a file"
          );
        }

        if (!document.accessLevel.length) {
          err["accessLevel"] = t(
            "orders:documents.errors.selectVisibility",
            "Please select at least a visibility level"
          );
        }

        if (!isEmpty(err)) {
          errors.push({ formId: document._id || "", error: err });
        }
      }
      if (errors.length) {
        return setErrors(errors);
      }
      setDocuments([]);
      onClose(documents);
    }
  };

  return (
    <Dialog maxWidth={maxWidth} fullWidth open={openModal}>
      <DialogTitle>
        <Typography variant="h6">
          {t("common:documents.uploadMany", "Upload Documents")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Stack spacing={2}>
          {documents?.map((document, index) => (
            <div key={index}>
              <ShipmentDocumentForm
                id={index}
                shipmentLocations={shipmentLocations}
                key={index}
                document={document}
                onChange={handleChange}
                onUpload={handleUpload}
                errors={errors.find((e) => e.formId === document._id)}
                customerDocumentRequirements={customerDocumentRequirements}
              />
              <Divider sx={{ mt: 5 }} />
            </div>
          ))}
        </Stack>
        {!currentDocumentToEdit ? (
          <Stack
            direction="row"
            spacing={2}
            sx={{ m: 1, display: "flex", alignItems: "center" }}
          >
            <AddButton data-testid="shipment-documents-upload-modal-add-document"
              id="add-document-button"
              sx={{ height: 50, width: 20 }}
              onClick={handleAddDocument}
            >
              <AddIcon />
            </AddButton>
            <span>{t("common:documents.add", "Add Document")}</span>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button data-testid={testIds.shipmentDocumentsUploadModalCancel}
          autoFocus
          color="inherit"
          variant="contained"
          onClick={handleCancel}
        >
          {t("common:cancel", "Cancel")}
        </Button>
        <Button data-testid={testIds.shipmentDocumentsUploadModalSubmit}
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isUploading}
        >
          {isUploading ? (
            <CircularProgress size={20} />
          ) : (
            t("common:upload", "Upload")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
